import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { decode } from 'html-entities';
import { 
  Music, 
  X, 
  Search, 
  Play,
  Pause,
  Upload,
  Grid,
  List,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Volume2,
  Download,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { wpMedia } from "../../lib/wordpress";
import Toast from "../shared/Toast";
import { scrollToSection } from '../../utils/scroll';
import { Pagination } from '../shared/Pagination';

interface AudioBrowserProps {
  onSelect: (url: string) => void;
  onClose: () => void;
  isAdmin?: boolean;
}

const ITEMS_PER_PAGE = 12;

export const AudioBrowser: React.FC<AudioBrowserProps> = ({ onSelect, onClose, isAdmin = false }) => {
  const [view, setView] = useState<'grid' | 'list'>('list');
  const [audios, setAudios] = useState<any[]>([]);
  const [allAudios, setAllAudios] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentAudioUrlRef = useRef<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Save current scroll position and disable scrolling on main content
    const scrollY = window.scrollY;
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.paddingRight = '15px'; // Prevent layout shift
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    
    audioRef.current = new Audio();
    audioRef.current.addEventListener('ended', () => {
      setIsPlaying(false);
      setPlayingId(null);
    });

    audioRef.current.addEventListener('error', () => {
      setIsPlaying(false);
      setPlayingId(null);
      setLoadingAudio(false);
    });

    audioRef.current.addEventListener('canplaythrough', () => {
      setLoadingAudio(false);
    });

    return () => {
      // Restore scrolling and position
      const scrollY = document.body.style.top;
      document.documentElement.style.overflow = '';
      document.documentElement.style.paddingRight = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
      
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current.remove();
      }
    };
  }, []);

  const loadAudios = useCallback(async () => {
    try {
      setLoading(true);
      const { items } = await wpMedia.listMedia({
        page: 1,
        per_page: 100,
        search: searchQuery,
        orderby: 'date',
        order: 'desc'
      });

      const audioFiles = items.filter(item => {
        const mimeType = (item.mime_type || '').toLowerCase();
        return mimeType.startsWith('audio/') || mimeType === 'video/mp4';
      });

      setAllAudios(audioFiles);

      const start = (currentPage - 1) * ITEMS_PER_PAGE;
      const end = start + ITEMS_PER_PAGE;
      setAudios(audioFiles.slice(start, end));
      setTotalPages(Math.ceil(audioFiles.length / ITEMS_PER_PAGE));
    } catch (err) {
      console.error('Error loading audio files:', err);
      setError('Failed to load audio files. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    loadAudios();
  }, [loadAudios]);

  useEffect(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    setAudios(allAudios.slice(start, end));
    
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [currentPage, allAudios]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    try {
      setUploading(true);
      const file = files[0];
      
      const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/mp4', 'video/mp4'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Please upload an audio file (MP3, WAV, OGG, M4A) or MP4 video.');
      }

      const result = await wpMedia.uploadMedia(file);
      setAllAudios(prev => [result, ...prev]);
      
      const start = (currentPage - 1) * ITEMS_PER_PAGE;
      const end = start + ITEMS_PER_PAGE;
      setAudios(allAudios.slice(start, end));
      setTotalPages(Math.ceil((allAudios.length + 1) / ITEMS_PER_PAGE));
    } catch (err: any) {
      console.error('Error uploading file:', err);
      setError(err.message || 'Failed to upload audio file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this audio file?')) return;

    try {
      await wpMedia.deleteMedia(id);
      const newAllAudios = allAudios.filter(audio => audio.id !== id);
      setAllAudios(newAllAudios);
      
      const start = (currentPage - 1) * ITEMS_PER_PAGE;
      const end = start + ITEMS_PER_PAGE;
      setAudios(newAllAudios.slice(start, end));
      setTotalPages(Math.ceil(newAllAudios.length / ITEMS_PER_PAGE));
      
      if (playingId === id) {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        setPlayingId(null);
        setIsPlaying(false);
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Failed to delete audio file. Please try again.');
    }
  };

  const handlePlayPause = async (id: number, audioUrl: string) => {
    if (!audioRef.current) return;

    try {
      setError(null);

      if (playingId === id) {
        // Same audio - toggle play/pause
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          setLoadingAudio(true);
          await audioRef.current.play();
          setIsPlaying(true);
        }
      } else {
        // Different audio - stop current and play new
        audioRef.current.pause();
        setIsPlaying(false);
        setLoadingAudio(true);
        
        // If it's a new audio URL, update the source
        if (currentAudioUrlRef.current !== audioUrl) {
          audioRef.current.src = audioUrl;
          currentAudioUrlRef.current = audioUrl;
          
          // Wait for audio to load
          try {
            await new Promise((resolve, reject) => {
              if (audioRef.current) {
                audioRef.current.onloadeddata = resolve;
                audioRef.current.onerror = reject;
              }
            });
          } catch (error) {
            throw new Error('Failed to play audio file.');
          }
        }
        
        await audioRef.current.play();
        setPlayingId(id);
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setPlayingId(null);
      setIsPlaying(false);
      setLoadingAudio(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    scrollToSection('audio-browser-content');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-md z-[400] flex items-center justify-center overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-card/95 backdrop-blur-sm rounded-lg w-full max-w-6xl mx-4 shadow-2xl border border-primary/20 relative flex flex-col max-h-[90vh] z-[401]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex justify-between items-center p-6 bg-card/95 backdrop-blur-sm border-b border-primary/10">
          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
            Galeria de Áudio
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary/80 rounded-full transition-all duration-200 hover:scale-110"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div id="audio-browser-content" className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0">
          {error && (
            <div className="mb-4 p-4 bg-destructive/10 text-destructive rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : audios.length === 0 ? (
            <div className="text-center text-gray-400 p-8">
              Nenhum arquivo de áudio encontrado.
            </div>
          ) : (
            <div className="space-y-4">
              {audios.map((audio) => (
                <div
                  key={audio.id}
                  className="bg-secondary/50 rounded-lg p-4 hover:bg-secondary/80 transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium mb-2">{decode(audio.title.rendered)}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Volume2 className="w-4 h-4" />
                          {audio.media_details?.filesize
                            ? `${Math.round(audio.media_details.filesize / 1024 / 1024 * 10) / 10} MB`
                            : 'Unknown size'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePlayPause(audio.id, audio.source_url)}
                        className="p-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors"
                        title={playingId === audio.id && isPlaying ? 'Pausar' : 'Reproduzir'}
                        disabled={loadingAudio}
                      >
                        {loadingAudio && playingId === audio.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : playingId === audio.id && isPlaying ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                      <a
                        href={audio.source_url}
                        download
                        className="p-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => onSelect(audio.source_url)}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        Selecionar
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => handleDelete(audio.id)}
                          className="p-2 text-destructive hover:bg-secondary rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
        
        <div className="sticky bottom-0 z-10 flex justify-end gap-4 p-6 bg-card/95 backdrop-blur-sm border-t border-primary/10">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-secondary/80 text-secondary-foreground rounded-lg hover:bg-secondary/60 transition-all duration-200 hover:scale-105 border border-primary/10"
          >
            Cancelar
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};